import { useState } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { useMutation } from '@apollo/client';
import { yupResolver } from '@hookform/resolvers';
import dayjs from 'dayjs';
// Api
import { CREATE_UNREGISTERED_CONTACT } from 'api/mentions/mutations';
import { CREATE_STREAM } from 'api/streams/mutations';
// Types
import {
  CreateStream,
  CreateStream_createStream,
  CreateStreamVariables,
} from 'api/streams/types/CreateStream';
import {
  CreateUnregisteredContact,
  CreateUnregisteredContactVariables,
} from 'api/mentions/types/CreateUnregisteredContact';
import { StreamFields } from 'api/streams/fragments';
import { GetStreamExtended_stream_mentions } from 'api/streams/types/GetStreamExtended';
// Constants
import { TimeZoneOption } from 'constants/timeZone';
// Helpers
import {
  getTimeZoneObject,
  toLegacyScheduleDateString,
} from 'helpers/timeZone';
import {
  formatHashtagInput,
  REPEAT_STREAM_OPTIONS,
  RepeatStreamOption,
} from 'helpers/streams';
import { getProperErrorMessage } from 'helpers/errors';
import { formatMentionsInput } from 'helpers/mentions';
import { createEditStreamValidationSchema } from 'helpers/validation';
// Hooks
import { useOnboardingContext } from 'hooks';
// Components
import FullScreenStepperModal from 'components/common3/FullScreenStepperModal/FullScreenStepperModal';
import InviteAndPromoteModal from 'components/ProfileSetup/components/InviteAndPromoteModal/InviteAndPromoteModal';
import StreamDetailsStep from 'components/ProfileSetup/components/common/CreateStreamWizard/steps/StreamDetailsStep/StreamDetailsStep';
import PricingStep from 'components/ProfileSetup/components/common/CreateStreamWizard/steps/PricingStep/PricingStep';
import AdvancedOptionsStep from 'components/ProfileSetup/components/common/CreateStreamWizard/steps/AdvancedOptionsStep/AdvancedOptionsStep';
import StreamSummaryPanel from 'components/ProfileSetup/components/common/CreateStreamWizard/StreamSummaryPanel/StreamSummaryPanel';
import WizardStepIndicator from 'components/CreatorSetup/StreamingSetup/CreateStreamSetup/WizardStepIndicator/WizardStepIndicator';
import {
  BoutDraft,
  getMatchcardError,
  mapBoutsToMatchupsInput,
} from 'components/ProfileSetup/components/common/CreateStreamWizard/steps/AdvancedOptionsStep/types';
import { showToast } from 'components/common/Toast/Toast';
// Styles
import styles from './CreateStreamSetup.module.scss';

export type FormInputs = {
  name: string | null;
  description: string;
  scheduleDate: Date | string;
  timeZone: TimeZoneOption;
  price: string;
  isFree: boolean;
  isHidden: boolean;
  image: File | null;
  previewImage: string;
  repeatsEveryEnum: RepeatStreamOption;
  hashtags?: string[];
  mentions?: GetStreamExtended_stream_mentions[] | null;
  matchcardEnabled: boolean;
  bouts: BoutDraft[];
  fanPollingEnabled: boolean;
  sharePickGraphicsEnabled: boolean;
  postEventContentHubEnabled: boolean;
  affiliateEnabled: boolean;
  affiliateCommissionPercent: string;
};

const STREAM_DETAILS_STEP = 1;
const PRICING_STEP = 2;
const ADVANCED_OPTIONS_STEP = 3;

// Validated when leaving a step; the full schema spans all steps.
const STEP_FIELDS: Record<number, (keyof FormInputs)[]> = {
  [STREAM_DETAILS_STEP]: [
    'name',
    'description',
    'scheduleDate',
    'timeZone',
    'repeatsEveryEnum',
    'hashtags',
    'previewImage',
    'image',
    'isHidden',
  ],
  [PRICING_STEP]: [
    'price',
    'isFree',
    'affiliateEnabled',
    'affiliateCommissionPercent',
  ],
};

const CreateStreamSetup = () => {
  const {
    showCreateStreamModal,
    setCreateStreamDone,
    setShowCreateStreamModal,
  } = useOnboardingContext();

  const handleClose = () => {
    setShowCreateStreamModal(false);
  };

  // Closing Share & Promote leaves the creator where they were (e.g. My Streams)
  // instead of routing them away to the dashboard.
  const handleSuccess = () => {
    setCreateStreamDone(true);
    setShowInviteAthletes(false);
    handleClose();
  };

  const [
    streamData,
    setStreamData,
  ] = useState<CreateStream_createStream | null>(null);
  const [showInviteAthletes, setShowInviteAthletes] = useState(false);
  const [step, setStep] = useState(1);
  const defaultTimeZone: TimeZoneOption = getTimeZoneObject(null);

  const defaultValues = {
    name: '',
    description: '',
    scheduleDate: '',
    timeZone: defaultTimeZone,
    repeatsEveryEnum: REPEAT_STREAM_OPTIONS[0],
    price: '',
    isFree: false,
    isHidden: false,
    previewImage: '',
    image: null,
    mentions: [],
    matchcardEnabled: false,
    bouts: [],
    fanPollingEnabled: false,
    sharePickGraphicsEnabled: false,
    postEventContentHubEnabled: false,
    affiliateEnabled: false,
    affiliateCommissionPercent: '',
  };

  const methods = useForm<FormInputs>({
    resolver: yupResolver(createEditStreamValidationSchema(false)),
    defaultValues: defaultValues,
    shouldUnregister: false,
  });

  const [createUnregisteredContact] = useMutation<
    CreateUnregisteredContact,
    CreateUnregisteredContactVariables
  >(CREATE_UNREGISTERED_CONTACT);

  const [createStream, { loading }] = useMutation<
    CreateStream,
    CreateStreamVariables
  >(CREATE_STREAM, {
    update(cache, { data }) {
      cache.modify({
        fields: {
          myStreams(existingStreams = {}) {
            const newStreamRef = cache.writeFragment({
              data: data?.createStream,
              fragment: StreamFields,
            });

            return {
              ...existingStreams,
              entities: [newStreamRef, ...existingStreams.entities],
            };
          },
        },
      });
    },
  });

  // Matchups and fan engagement are paid-stream features: a free stream has no
  // Advanced Options step and schedules straight from Pricing.
  const hasAdvancedOptions = !methods.watch('isFree');
  const stepLabels = hasAdvancedOptions
    ? ['Stream Details', 'Pricing & Affiliate', 'Advanced Options']
    : ['Stream Details', 'Pricing & Affiliate'];
  const totalStepsNumber = stepLabels.length;

  const handleFormSubmit = methods.handleSubmit(
    async (values): Promise<void> => {
      const hasMatchcard = !values.isFree && values.matchcardEnabled;
      const matchcardError = hasMatchcard
        ? getMatchcardError(values.bouts)
        : undefined;

      if (matchcardError) {
        showToast({ message: matchcardError, type: 'error' });
        return;
      }

      const timeZoneOffset = dayjs
        .tz(
          dayjs(values.scheduleDate).format('YYYY-MM-DD HH:mm'),
          values.timeZone.tzName
        )
        .utcOffset();

      const variables = {
        sponsors: [],
        name: values.name,
        repeatingTitle: values.repeatsEveryEnum.value ? values.name : null,
        repeatsEveryEnum: values.repeatsEveryEnum.value,
        description: values.description,
        ...(values.image
          ? {
              image: values.image,
            }
          : {}),
        scheduleDate: toLegacyScheduleDateString(values.scheduleDate),
        timeZone: {
          tzCode: values.timeZone.tzCode,
          offset: timeZoneOffset,
        },
        isPPV: !values.isFree,
      };

      const createUnregisteredContactInput =
        (values.mentions as any)?.filter((mention) => !mention.targetType) ||
        [];

      const unregisterContactsData = await createUnregisteredContact({
        variables: {
          input: createUnregisteredContactInput,
        },
      });

      const newUnregisteredContacts =
        unregisterContactsData.data?.createUnregisteredContact?.map(
          (contact) => ({
            unregisteredContactId: contact.id,
          })
        ) || [];

      const oldMentions =
        values.mentions?.filter((mention) => mention.targetType) || [];
      const formatedOldMentions = formatMentionsInput(oldMentions);

      const matchups = hasMatchcard
        ? mapBoutsToMatchupsInput(values.bouts)
        : [];
      const hasMatchups = matchups.length > 0;
      const fanPollingEnabled = hasMatchups && values.fanPollingEnabled;
      const sharePickGraphicsEnabled =
        hasMatchups && values.sharePickGraphicsEnabled;
      const postEventContentHubEnabled = !values.isFree;
      const affiliateEnabled = !values.isFree && values.affiliateEnabled;

      try {
        const createStreamResponse = await createStream({
          variables: {
            input: {
              isFree: values.isFree,
              isHidden: values.isHidden,
              hashtagInputs: values.hashtags
                ? formatHashtagInput(values.hashtags)
                : [],
              mentionsInputs: [
                ...formatedOldMentions,
                ...newUnregisteredContacts,
              ],
              ...(!values.isFree && {
                price: Number(values.price),
              }),
              ...(matchups.length > 0 && { matchups }),
              fanPollingEnabled,
              sharePickGraphicsEnabled,
              postEventContentHubEnabled,
              affiliateEnabled,
              affiliateCommissionPercent: affiliateEnabled
                ? Number(values.affiliateCommissionPercent)
                : null,
              ...variables,
              sponsors: [],
            },
          },
        });

        setStreamData(createStreamResponse?.data?.createStream || null);
        showToast({
          message: 'Stream successfully created',
        });
        setShowInviteAthletes(true);
      } catch (error) {
        console.log('error', error);
        showToast({
          message: getProperErrorMessage(
            error,
            `Something went wrong. (${error?.message})`
          ),
          type: 'error',
        });
      }
    }
  );

  const goToNextStep = async () => {
    const isStepValid = await methods.trigger(STEP_FIELDS[step]);

    if (isStepValid) {
      setStep((currentStep) => currentStep + 1);
    }
  };

  const goToPreviousStep = () => {
    setStep((currentStep) => currentStep - 1);
  };

  const footer =
    step === totalStepsNumber
      ? {
          stepNumber: step,
          totalStepsNumber,
          showPrevButton: true,
          prevButtonProps: {
            onClick: goToPreviousStep,
            variant: 'secondary' as const,
            subVariant: 'outlined' as const,
          },
          submitButtonText: 'Schedule',
          submitButtonProps: {
            onClick: handleFormSubmit,
            variant: 'primary' as const,
            loading,
          },
        }
      : {
          stepNumber: step,
          totalStepsNumber,
          showPrevButton: step > 1,
          prevButtonProps: {
            onClick: goToPreviousStep,
            variant: 'secondary' as const,
            subVariant: 'outlined' as const,
          },
          submitButtonText: 'Continue',
          submitButtonProps: {
            onClick: goToNextStep,
            variant: 'primary' as const,
          },
        };

  return (
    <>
      {/* Stepper is hidden once the stream is created, so only Share & Promote is visible. */}
      <FullScreenStepperModal
        title="CREATE STREAM"
        open={showCreateStreamModal && !showInviteAthletes}
        onClose={handleClose}
        footer={footer}
      >
        <FormProvider {...methods}>
          <div className={styles.content}>
            <WizardStepIndicator steps={stepLabels} currentStep={step} />
            {step === STREAM_DETAILS_STEP && <StreamDetailsStep />}
            {step === PRICING_STEP && <PricingStep />}
            {step === ADVANCED_OPTIONS_STEP && (
              <AdvancedOptionsStep aside={<StreamSummaryPanel />} />
            )}
          </div>
        </FormProvider>
      </FullScreenStepperModal>

      {streamData && showInviteAthletes && (
        <InviteAndPromoteModal
          show={showInviteAthletes}
          stream={streamData}
          onClose={handleSuccess}
        />
      )}
    </>
  );
};

export default CreateStreamSetup;
